import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import useToast from 'hooks/useToast'
import { fulfilPayout } from 'submission/lib/partner-api'
import type { Partner } from './types'
import type { PayoutRequest } from './types'
import { formatUSDC } from './format'

type Props = {
  payout: PayoutRequest | null
  partner?: Partner
  onClose: () => void
  onFulfilled: () => void
  onError?: (e: Error) => void
}

export const TX_HASH = /^0x[0-9a-fA-F]{64}$/

const inputCls =
  'w-full border border-neutral-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-primary'

const FulfilPayoutModal: React.FC<Props> = ({
  payout,
  partner,
  onClose,
  onFulfilled,
  onError,
}) => {
  const { showToast } = useToast()
  const [hash, setHash] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setHash('')
    setError(null)
  }, [payout?.id])

  const valid = TX_HASH.test(hash.trim())

  const submit = async () => {
    if (!payout || !valid) return
    setSubmitting(true)
    setError(null)
    try {
      await fulfilPayout(payout.id, hash.trim())
      showToast({ type: 'success', message: `Payout #${payout.id} fulfilled` })
      onFulfilled()
    } catch (e) {
      const err = e as Error
      setError(err.message)
      showToast({ type: 'error', message: err.message })
      onError?.(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={payout !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            Fulfil payout{payout ? ` #${payout.id}` : ''}
          </DialogTitle>
        </DialogHeader>
        {payout && (
          <div className="space-y-3 text-sm">
            <div>
              Amount <strong>{formatUSDC(payout.amount_usdc)}</strong> to{' '}
              <strong>
                {partner?.handle ?? `partner #${payout.partner_id}`}
              </strong>
              . Send the transfer first, then paste the transaction hash.
            </div>
            <div className="rounded border border-neutral-200 p-2 text-xs">
              {partner?.payout_wallet ? (
                <>
                  Wallet{' '}
                  <span className="font-mono">{partner.payout_wallet}</span>
                  {partner.payout_chain_id
                    ? ` · chain ${partner.payout_chain_id}`
                    : ''}
                </>
              ) : (
                <span className="text-red-600">No payout wallet on file</span>
              )}
            </div>
            <label className="block">
              Transaction hash
              <input
                className={inputCls}
                value={hash}
                onChange={(e) => setHash(e.target.value)}
                placeholder="0x…"
              />
            </label>
            {error && <div className="text-red-600">{error}</div>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Close
          </Button>
          <Button onClick={submit} disabled={!valid || submitting}>
            Confirm fulfil
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default FulfilPayoutModal
